import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import '../lib/apiConfig'; // Initialize API config
import { CorporateEntityService } from '../app/api/services/CorporateEntityService';
import { LocationsService } from '../app/api/services/LocationsService';
import { IndustriesService } from '../app/api/services/IndustriesService';
import { CountriesService } from '../app/api/services/CountriesService';
import { CertificationsService } from '../app/api/services/CertificationsService';
import { ProductsService } from '../app/api/services/ProductsService';
import { ServicesService } from '../app/api/services/ServicesService';
import { UsersService } from '../app/api/services/UsersService';
import { withAuth, getAuthToken, waitForSessionReady } from '../lib/auth';
import { cn } from '../lib/utils';
import { AsYouType } from 'libphonenumber-js';

/** Delay before redirecting unauthenticated user to signup (ms). */
const UNAUTHENTICATED_REDIRECT_DELAY_MS = 2000;

// Simplified onboarding steps
const STEPS = {
    BASIC_INFO: 1,        // Step 1: User type selection
    PERSONAL_INFO: 2,     // Step 2: Name editing (for BROWSER only)
    CORPORATE_ENTITY: 3,  // Step 3: Company profile (if SUPPLIER/BUYER)
    LOCATION: 4,          // Step 4: Location details
    PRODUCTS: 5,          // Step 5: Products & Services
    MANAGER_ROLE: 6,     // Step 6: Manager Role
    CERTIFICATIONS: 7,    // Step 7: Certifications (Suppliers only)
    SOCIAL_MEDIA: 8,      // Step 8: Social Media
};

// Role configuration - centralized source of truth for role-specific behavior
const ROLE_CONFIG = {
    BROWSER: {
        name: 'BROWSER',
        displayName: 'Browser',
        description: 'Browse the directory',
        steps: [STEPS.BASIC_INFO, STEPS.PERSONAL_INFO],
        firstStepAfterBasic: STEPS.PERSONAL_INFO,
        completionStep: STEPS.PERSONAL_INFO,
        requiresCompany: false,
        requiresLocation: false,
        requiresCertifications: false,
    },
    BUYER: {
        name: 'BUYER',
        displayName: 'Buyer',
        description: 'I want to find suppliers',
        steps: [STEPS.BASIC_INFO, STEPS.CORPORATE_ENTITY, STEPS.LOCATION, STEPS.PRODUCTS, STEPS.MANAGER_ROLE, STEPS.SOCIAL_MEDIA],
        firstStepAfterBasic: STEPS.CORPORATE_ENTITY,
        completionStep: STEPS.SOCIAL_MEDIA,
        requiresCompany: true,
        requiresLocation: true,
        requiresCertifications: false,
    },
    SUPPLIER: {
        name: 'SUPPLIER',
        displayName: 'Supplier',
        description: 'I want to list my services',
        steps: [STEPS.BASIC_INFO, STEPS.CORPORATE_ENTITY, STEPS.LOCATION, STEPS.PRODUCTS, STEPS.MANAGER_ROLE, STEPS.CERTIFICATIONS, STEPS.SOCIAL_MEDIA],
        firstStepAfterBasic: STEPS.CORPORATE_ENTITY,
        completionStep: STEPS.SOCIAL_MEDIA,
        requiresCompany: true,
        requiresLocation: true,
        requiresCertifications: true,
    },
};

// Helper functions for role-based logic
const getRoleConfig = (userType) => {
    return ROLE_CONFIG[userType] || null;
};

const getNextStep = (currentStep, userType, hasRoleFromSignup = false) => {
    const config = getRoleConfig(userType);
    if (!config) return null;

    // Special handling for PRODUCTS step
    if (currentStep === STEPS.PRODUCTS) {
        if (hasRoleFromSignup) {
            return userType === 'SUPPLIER' ? STEPS.CERTIFICATIONS : STEPS.SOCIAL_MEDIA;
        }
        return STEPS.MANAGER_ROLE;
    }

    // Special handling for MANAGER_ROLE step
    if (currentStep === STEPS.MANAGER_ROLE) {
        return userType === 'SUPPLIER' ? STEPS.CERTIFICATIONS : STEPS.SOCIAL_MEDIA;
    }

    // Find current step index in role's steps array
    const currentIndex = config.steps.indexOf(currentStep);
    if (currentIndex === -1 || currentIndex === config.steps.length - 1) {
        return null; // Already at last step
    }

    return config.steps[currentIndex + 1];
};

const getPreviousStep = (currentStep, userType) => {
    const config = getRoleConfig(userType);
    if (!config) return null;

    // Special handling for SOCIAL_MEDIA step
    if (currentStep === STEPS.SOCIAL_MEDIA) {
        return userType === 'SUPPLIER' ? STEPS.CERTIFICATIONS : STEPS.MANAGER_ROLE;
    }

    // Find current step index in role's steps array
    const currentIndex = config.steps.indexOf(currentStep);
    if (currentIndex <= 0) {
        return null; // Already at first step
    }

    return config.steps[currentIndex - 1];
};


function Onboarding() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(STEPS.BASIC_INFO);
    const [loading, setLoading] = useState(true); // Start with loading true
    const [error, setError] = useState('');
    const [success] = useState('');
    const [userId, setUserId] = useState(() => {
        // Initialize from localStorage if available
        const stored = localStorage.getItem('Id');
        return stored ? parseInt(stored, 10) : null;
    });

    // User type selection state - single source of truth
    const [userType, setUserType] = useState(() => {
        // Initialize from localStorage if available
        return localStorage.getItem('userType') || '';
    });

    // Legacy support variables
    const goal = localStorage.getItem('goal') || '';
    const role = localStorage.getItem('role') || '';

    // Get role configuration for current user type
    const roleConfig = getRoleConfig(userType);

    // Listen for storage changes (when userId is set after sync)
    useEffect(() => {
        const handleStorageChange = () => {
            const stored = localStorage.getItem('Id');
            if (stored) {
                const newUserId = parseInt(stored, 10);
                if (newUserId !== userId) {
                    setUserId(newUserId);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        // Also check immediately in case it was set in the same window
        handleStorageChange();

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [userId]);

    // Step 1: Corporate Entity
    const [legalName, setLegalName] = useState('');
    const [website, setWebsite] = useState('');
    const [phone, setPhone] = useState('');
    const [companySize, setCompanySize] = useState('');
    const [industry, setIndustry] = useState('');
    const [industrySearch, setIndustrySearch] = useState('');
    const [showIndustryDropdown, setShowIndustryDropdown] = useState(false);
    const [moq, setMoq] = useState('');
    const [moqQuestion, setMoqQuestion] = useState('');
    const [industries, setIndustries] = useState([]);
    const [filteredIndustries, setFilteredIndustries] = useState([]);

    // Step 2: Location
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [countryCode, setCountryCode] = useState('');
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);

    // Step 3: Products & Services
    const [products, setProducts] = useState([]);
    const [services, setServices] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedServices, setSelectedServices] = useState([]);

    // Personal info (for browsers)
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');

    // Step 4: Manager Role
    const [managerRole, setManagerRole] = useState('');
    const [customRole, setCustomRole] = useState('');

    // Step 5: Certifications
    const [availableDiversityCerts, setAvailableDiversityCerts] = useState([]);
    const [availableOtherCerts, setAvailableOtherCerts] = useState([]);
    const [selectedDiversityCerts, setSelectedDiversityCerts] = useState([]);
    const [selectedOtherCerts, setSelectedOtherCerts] = useState([]);

    // Step 6: Social Media
    const [socialMediaAccounts, setSocialMediaAccounts] = useState({
        linkedin: '',
        facebook: '',
        twitter: '',
        instagram: '',
        youtube: '',
    });

    // Claimed profile: pre-fill and update instead of create
    const [existingCompanyId, setExistingCompanyId] = useState(null);
    const [existingLocationId, setExistingLocationId] = useState(null);

    /** Tracks whether this component is still mounted; avoids setState/navigate after unmount. */
    const isMountedRef = useRef(true);

    // Load data on mount - sync user and check auth
    useEffect(() => {
        isMountedRef.current = true;
        let redirectTimeoutId = null;

        const checkAuthAndSync = async () => {
            try {
                await waitForSessionReady();
                const token = await getAuthToken();

                if (!isMountedRef.current) return;
                if (!token) {
                    setError('You are not authenticated. Please log in to continue.');
                    setLoading(false);
                    redirectTimeoutId = setTimeout(() => {
                        if (isMountedRef.current) {
                            navigate('/signup');
                        }
                    }, UNAUTHENTICATED_REDIRECT_DELAY_MS);
                    return;
                }

                // Sync user to database (safe to call multiple times)
                const { syncUser } = await import('../lib/authSync');
                const syncResult = await syncUser();

                if (!isMountedRef.current) return;

                if (syncResult.success && syncResult.data) {
                    // Store user ID from sync result
                    const syncedUserId = syncResult.data.userid.toString();
                    localStorage.setItem('Id', syncedUserId);
                    if (syncResult.data.userType) {
                        localStorage.setItem('userType', syncResult.data.userType);
                        // Update userType state
                        setUserType(syncResult.data.userType);
                    }

                    // Check if profile is already complete - redirect immediately if so
                    if (syncResult.data.profile_complete === true) {
                        navigate('/directory', { replace: true });
                        return;
                    }

                    // Update state
                    setUserId(parseInt(syncedUserId, 10));
                } else {
                    // Sync failed - log the full error for debugging
                    console.error('Failed to sync user:', {
                        error: syncResult.error,
                        success: syncResult.success,
                        data: syncResult.data
                    });

                    if (!userId) {
                        // Only show error if we truly don't have a userId
                        setError(`Failed to load your account: ${syncResult.error || 'Unknown error'}. Please try refreshing the page.`);
                    } else {
                        // If we have userId, just log the warning
                        console.warn('Sync failed but user ID exists, continuing...');
                    }
                }
            } catch (error) {
                console.error('Error checking authentication:', error);
                if (isMountedRef.current) {
                    if (!userId) {
                        setError('An error occurred. Please try refreshing the page.');
                    }
                }
            } finally {
                if (isMountedRef.current) {
                    setLoading(false);
                }
            }
        };

        // Always check auth and sync (safe to call multiple times)
        checkAuthAndSync();

        return () => {
            isMountedRef.current = false;
            if (redirectTimeoutId) {
                clearTimeout(redirectTimeoutId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount - intentionally only run on mount

    // Fetch user data to populate name fields and check if profile is complete
    useEffect(() => {
        const fetchUserData = async () => {
            if (userId) {
                try {
                    const userData = await withAuth(() =>
                        UsersService.getApiV2Users(userId)
                    );
                    if (userData && userData.data) {
                        // Check if profile is already complete
                        if (userData.data.profile_complete === true) {
                            // Profile is complete, redirect to directory
                            navigate('/directory', { replace: true });
                            return;
                        }

                        // If userType is set in database, update state and skip BASIC_INFO
                        if (userData.data.userType) {
                            setUserType(userData.data.userType);
                            localStorage.setItem('userType', userData.data.userType);

                            // Skip BASIC_INFO step if userType is already set and we're on BASIC_INFO
                            setCurrentStep((prevStep) => {
                                if (prevStep === STEPS.BASIC_INFO) {
                                    const config = getRoleConfig(userData.data.userType);
                                    return config ? config.firstStepAfterBasic : prevStep;
                                }
                                return prevStep;
                            });
                        }

                        // Profile not complete, populate name fields
                        if (userData.data.firstname) {
                            setFirstname(userData.data.firstname);
                        }
                        if (userData.data.lastname) {
                            setLastname(userData.data.lastname);
                        }
                    }
                } catch (error) {
                    console.error('Error fetching user data:', error);
                    // Set defaults if fetch fails
                    setFirstname('User');
                    setLastname('Name');
                }
            }
        };
        fetchUserData();
    }, [userId, navigate]);

    // Initialize manager role and load data
    useEffect(() => {
        // Initialize manager role from signup
        if (role) {
            setManagerRole(role);
        }

        // Determine MOQ question based on user type
        if (goal === 'buying') {
            setMoqQuestion('Do you know the volume or quantity you require?');
        } else if (goal === 'listing') {
            setMoqQuestion('Do you have a minimum volume or minimum order quantity (MOQ)?');
        }

        loadIndustries();
        loadCountries();
        loadProducts();
        loadServices();
        if (roleConfig?.requiresCertifications) {
            loadCertifications();
        }

        // Load saved progress and form data from localStorage
        const savedStep = localStorage.getItem('onboarding_step');
        const savedUserType = localStorage.getItem('userType');

        // If userType is already set, skip BASIC_INFO step
        if (savedUserType) {
            const config = getRoleConfig(savedUserType);
            if (config) {
                if (savedStep) {
                    const stepNum = parseInt(savedStep, 10);
                    // Only use saved step if it's not BASIC_INFO
                    if (stepNum !== STEPS.BASIC_INFO) {
                        setCurrentStep(stepNum);
                    } else {
                        setCurrentStep(config.firstStepAfterBasic);
                    }
                } else {
                    setCurrentStep(config.firstStepAfterBasic);
                }
            } else if (savedStep) {
                setCurrentStep(parseInt(savedStep, 10));
            } else {
                setCurrentStep(STEPS.BASIC_INFO);
            }
        } else if (savedStep) {
            setCurrentStep(parseInt(savedStep, 10));
        } else {
            // No saved step, start at BASIC_INFO
            setCurrentStep(STEPS.BASIC_INFO);
        }

        // Load saved form data
        const savedFormData = localStorage.getItem('onboarding_draft');
        if (savedFormData) {
            try {
                const draft = JSON.parse(savedFormData);
                if (draft.legalName) setLegalName(draft.legalName);
                if (draft.website) setWebsite(draft.website);
                if (draft.phone) {
                    // Format phone number if it's stored as digits only
                    const digits = draft.phone.replace(/\D/g, '');
                    if (digits) {
                        const formatter = new AsYouType('US');
                        const formatted = formatter.input(digits);
                        setPhone(formatted);
                    } else {
                        setPhone(draft.phone);
                    }
                }
                if (draft.companySize) setCompanySize(draft.companySize);
                if (draft.industry) setIndustry(draft.industry);
                if (draft.moq) setMoq(draft.moq);
                if (draft.address) setAddress(draft.address);
                if (draft.city) setCity(draft.city);
                if (draft.state) setState(draft.state);
                if (draft.postalCode) setPostalCode(draft.postalCode);
                if (draft.countryCode) setCountryCode(draft.countryCode);
                if (draft.managerRole) setManagerRole(draft.managerRole);
                if (draft.customRole) setCustomRole(draft.customRole);
                if (draft.selectedProducts) setSelectedProducts(draft.selectedProducts);
                if (draft.selectedServices) setSelectedServices(draft.selectedServices);
                if (draft.selectedDiversityCerts) setSelectedDiversityCerts(draft.selectedDiversityCerts);
                if (draft.selectedOtherCerts) setSelectedOtherCerts(draft.selectedOtherCerts);
                if (draft.socialMediaAccounts) setSocialMediaAccounts(draft.socialMediaAccounts);
            } catch (error) {
                console.error('Error loading saved form data:', error);
            }
        }

        // If user just claimed a profile, load that company and pre-fill (don't overwrite draft if present)
        const companyIdFromStorage = localStorage.getItem('company_id');
        if (companyIdFromStorage && userId && !savedFormData) {
            const loadClaimedCompany = async () => {
                try {
                    const cid = parseInt(companyIdFromStorage, 10);
                    if (isNaN(cid)) return;
                    const response = await withAuth(() =>
                        CorporateEntityService.getApiV2CorporateEntity1(cid)
                    );
                    const data = response?.data;
                    if (!data || data.managerid !== userId) return;

                    setExistingCompanyId(cid);
                    if (data.legalname) setLegalName(data.legalname);
                    if (data.website) setWebsite(data.website);
                    if (data.phone) {
                        const digits = String(data.phone || '').replace(/\D/g, '');
                        if (digits) {
                            const formatter = new AsYouType('US');
                            setPhone(formatter.input(digits));
                        } else {
                            setPhone(data.phone || '');
                        }
                    }
                    if (data.size) setCompanySize(data.size);
                    if (data.industry) setIndustry(data.industry);
                    if (data.MOQ) setMoq(data.MOQ);

                    const firstLocation = data.location?.[0];
                    if (firstLocation) {
                        setExistingLocationId(firstLocation.id);
                        if (firstLocation.address) setAddress(firstLocation.address);
                        if (firstLocation.city) setCity(firstLocation.city);
                        if (firstLocation.state) setState(firstLocation.state || '');
                        if (firstLocation.postal_code) setPostalCode(firstLocation.postal_code || '');
                        if (firstLocation.country_code) setCountryCode(firstLocation.country_code.toUpperCase());
                    }
                } catch (error) {
                    console.error('Error loading claimed company:', error);
                }
            };
            loadClaimedCompany();
        }
    }, [userId, navigate, goal, userType, role, roleConfig?.requiresCertifications]);

    // Filter industries
    useEffect(() => {
        if (industrySearch) {
            setFilteredIndustries(
                industries.filter((ind) =>
                    ind.indname?.toLowerCase().includes(industrySearch.toLowerCase())
                )
            );
            setShowIndustryDropdown(true);
        } else {
            setFilteredIndustries(industries);
        }
    }, [industrySearch, industries]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showIndustryDropdown && !event.target.closest('.industry-dropdown-container')) {
                setShowIndustryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showIndustryDropdown]);

    // Load states when country changes
    useEffect(() => {
        if (countryCode) {
            loadStates(countryCode);
            localStorage.setItem('country_code', countryCode);
        }
    }, [countryCode]);

    const loadIndustries = async () => {
        try {
            const response = await IndustriesService.getApiV2Industries();
            if (response.data) {
                setIndustries(response.data);
                setFilteredIndustries(response.data);
            }
        } catch (error) {
            console.error('Error loading industries:', error);
        }
    };

    const loadCountries = async () => {
        try {
            const response = await CountriesService.getApiV2Countries();
            if (response.data) {
                setCountries(response.data);
            }
        } catch (error) {
            console.error('Error loading countries:', error);
        }
    };

    const loadStates = async (countryCode) => {
        try {
            const response = await CountriesService.getApiV2CountriesStates(countryCode);
            if (response.data) {
                setStates(response.data);
            }
        } catch (error) {
            console.error('Error loading states:', error);
        }
    };

    const loadCertifications = async () => {
        try {
            const [diversityResponse, otherResponse] = await Promise.all([
                CertificationsService.getApiV2Certifications(undefined, undefined, 'diversity'),
                CertificationsService.getApiV2Certifications(undefined, undefined, 'standard'),
            ]);
            if (diversityResponse.data) {
                setAvailableDiversityCerts(diversityResponse.data);
            }
            if (otherResponse.data) {
                setAvailableOtherCerts(otherResponse.data);
            }
        } catch (error) {
            console.error('Error loading certifications:', error);
        }
    };

    const loadProducts = async () => {
        try {
            const response = await ProductsService.getApiV2Products();
            if (response.data) {
                setProducts(response.data);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        }
    };

    const loadServices = async () => {
        try {
            const response = await ServicesService.getApiV2Services();
            if (response.data) {
                setServices(response.data);
            }
        } catch (error) {
            console.error('Error loading services:', error);
        }
    };

    // Helper function to get clean numeric phone number (digits only)
    const getCleanPhoneNumber = (formattedPhone) => {
        return formattedPhone.replace(/\D/g, '');
    };

    // Save form data to localStorage
    const saveFormDataToLocalStorage = () => {
        const formData = {
            legalName,
            website,
            phone,
            companySize,
            industry,
            moq,
            address,
            city,
            state,
            postalCode,
            countryCode,
            selectedProducts,
            selectedServices,
            managerRole,
            customRole,
            selectedDiversityCerts,
            selectedOtherCerts,
            socialMediaAccounts,
        };
        localStorage.setItem('onboarding_draft', JSON.stringify(formData));
    };

    const handleBasicInfoSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!userType) {
            setError('Please select your user type');
            return;
        }

        // Save userType to localStorage (state already updated via onClick)
        localStorage.setItem('userType', userType);

        // Get next step based on role configuration
        const config = getRoleConfig(userType);
        if (config) {
            setCurrentStep(config.firstStepAfterBasic);
            localStorage.setItem('onboarding_step', config.firstStepAfterBasic.toString());
        }
    };

    const handlePersonalInfoSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!firstname.trim()) {
            setError('Please enter your first name');
            return;
        }

        if (!lastname.trim()) {
            setError('Please enter your last name');
            return;
        }

        try {
            setLoading(true);
            // Update userType, firstname, lastname, and profile_complete in database
            if (userId) {
                await withAuth(() =>
                    UsersService.patchApiV2Users(userId, {
                        userType: userType,
                        firstname: firstname.trim(),
                        lastname: lastname.trim(),
                        profile_complete: true
                    })
                );
            }
            // Redirect to directory
            navigate('/directory');
        } catch (error) {
            console.error('Error updating user profile:', error);
            setError('Failed to update your profile. Please try again.');
            setLoading(false);
        }
    };

    const handleCorporateEntitySubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!legalName.trim()) {
            setError('Please enter your company legal name');
            return;
        }

        if (!website.trim()) {
            setError('Please enter your company website');
            return;
        }

        // Validate and normalize website URL
        let normalizedWebsite = website.trim();
        if (!normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
            normalizedWebsite = 'https://' + normalizedWebsite;
        }
        if (!normalizedWebsite.match(/^https?:\/\/.+/)) {
            setError('Please enter a valid website URL (must start with http:// or https://)');
            return;
        }

        const cleanPhone = getCleanPhoneNumber(phone);
        if (!cleanPhone || cleanPhone.length < 10) {
            setError('Please enter a valid phone number (at least 10 digits)');
            return;
        }

        if (!companySize) {
            setError('Please select company size');
            return;
        }

        if (!industry) {
            setError('Please select an industry');
            return;
        }

        // Update website with normalized value
        setWebsite(normalizedWebsite);

        // Save to localStorage with normalized website and move to next step
        const formData = {
            legalName,
            website: normalizedWebsite,
            phone,
            companySize,
            industry,
            moq,
            address,
            city,
            state,
            postalCode,
            countryCode,
            managerRole,
            customRole,
            selectedDiversityCerts,
            selectedOtherCerts,
            socialMediaAccounts,
        };
        localStorage.setItem('onboarding_draft', JSON.stringify(formData));
        setCurrentStep(STEPS.LOCATION);
        localStorage.setItem('onboarding_step', STEPS.LOCATION.toString());
    };

    const handleLocationSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!address.trim()) {
            setError('Please enter your address');
            return;
        }

        if (!city.trim()) {
            setError('Please enter your city');
            return;
        }

        if (!countryCode) {
            setError('Please select a country');
            return;
        }

        // Save to localStorage and move to next step
        saveFormDataToLocalStorage();

        setCurrentStep(STEPS.PRODUCTS);
        localStorage.setItem('onboarding_step', STEPS.PRODUCTS.toString());
    };

    const handleProductsSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Products and services are optional, so we can proceed
        // Save to localStorage and move to next step
        saveFormDataToLocalStorage();

        // If we have a role from signup, skip manager role step
        const nextStep = getNextStep(STEPS.PRODUCTS, userType, role && role.trim());
        if (nextStep) {
            setCurrentStep(nextStep);
            localStorage.setItem('onboarding_step', nextStep.toString());
        }
    };

    const handleManagerRoleSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Use the role from signup if managerRole is empty, otherwise use what user entered
        const finalRole = managerRole === 'other'
            ? customRole
            : (managerRole || role);

        if (!finalRole || !finalRole.trim()) {
            setError('Please enter your role');
            return;
        }

        // Save to localStorage and move to next step
        saveFormDataToLocalStorage();
        const nextStep = getNextStep(STEPS.MANAGER_ROLE, userType);
        if (nextStep) {
            setCurrentStep(nextStep);
            localStorage.setItem('onboarding_step', nextStep.toString());
        }
    };

    const handleCertificationsSubmit = (e) => {
        e.preventDefault();
        setError('');

        // Certifications are optional, so we can proceed
        // Save to localStorage and move to next step
        saveFormDataToLocalStorage();
        setCurrentStep(STEPS.SOCIAL_MEDIA);
        localStorage.setItem('onboarding_step', STEPS.SOCIAL_MEDIA.toString());
    };

    const handleSocialMediaSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Social media is optional
        // Save to localStorage and submit onboarding
        saveFormDataToLocalStorage();

        try {
            // Submit onboarding data (no membership step)
            await submitAllOnboardingData();
            // If submission successful, complete onboarding
            handleOnboardingComplete();
        } catch (error) {
            // Error already set in submitAllOnboardingData
            console.error('Error completing onboarding:', error);
        }
    };

    // Submit all onboarding data to the API at once
    const submitAllOnboardingData = async () => {
        setLoading(true);
        setError('');

        try {
            // Validate required fields
            if (!legalName.trim()) {
                throw new Error('Please enter your company legal name');
            }
            if (!website.trim()) {
                throw new Error('Please enter your company website');
            }
            const cleanPhone = getCleanPhoneNumber(phone);
            if (!cleanPhone || cleanPhone.length < 10) {
                throw new Error('Please enter a valid phone number (at least 10 digits)');
            }
            if (!companySize) {
                throw new Error('Please select company size');
            }
            if (!industry) {
                throw new Error('Please select an industry');
            }
            if (!address.trim()) {
                throw new Error('Please enter your address');
            }
            if (!city.trim()) {
                throw new Error('Please enter your city');
            }
            if (!countryCode) {
                throw new Error('Please select a country');
            }

            // Validate and normalize website URL
            let normalizedWebsite = website.trim();
            if (!normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
                normalizedWebsite = 'https://' + normalizedWebsite;
            }
            if (!normalizedWebsite.match(/^https?:\/\/.+/)) {
                throw new Error('Please enter a valid website URL (must start with http:// or https://)');
            }

            // Get final manager role
            const finalRole = managerRole === 'other'
                ? customRole
                : (managerRole || role);

            if (!finalRole || !finalRole.trim()) {
                throw new Error('Please enter your role');
            }

            // Step 1: Create or update corporate entity (update when user claimed a profile)
            const corporateEntityData = {
                legal_name: legalName,
                website: normalizedWebsite,
                phone: cleanPhone,
                size: companySize,
                industry: industry,
                MOQ: moq || undefined,
                activeflag: true,
            };

            let corporateEntityId;
            if (existingCompanyId) {
                await withAuth(() =>
                    CorporateEntityService.patchApiV2CorporateEntity(existingCompanyId, corporateEntityData)
                );
                corporateEntityId = existingCompanyId;
            } else {
                const corporateEntityResponse = await withAuth(() =>
                    CorporateEntityService.postApiV2CorporateEntity(corporateEntityData)
                );
                corporateEntityId = corporateEntityResponse.data?.cid;
                if (!corporateEntityId) {
                    throw new Error('Failed to create corporate entity - no ID returned');
                }
            }

            // Step 2: Create or update location
            let locationId;
            if (existingLocationId) {
                await withAuth(() =>
                    LocationsService.patchApiV2LocationsAddress(existingLocationId, {
                        country_code: countryCode.toUpperCase(),
                        address: address,
                        city: city,
                        state: state || undefined,
                        postal_code: postalCode || undefined,
                    })
                );
                locationId = existingLocationId;
            } else {
                const locationResponse = await withAuth(() =>
                    LocationsService.postApiV2Locations({
                        cid: corporateEntityId,
                        managerid: userId,
                        country_code: countryCode.toUpperCase(),
                        address: address,
                        city: city,
                        state: state || undefined,
                        postal_code: postalCode || undefined,
                    })
                );
                locationId = locationResponse?.data?.id;
                if (!locationId) {
                    throw new Error('Failed to create location - no ID returned');
                }
            }

            // Step 3: Add products to location (if any selected)
            // buying is true for buyers, false for suppliers
            const isBuying = goal === 'buying';
            if (selectedProducts && selectedProducts.length > 0) {
                await withAuth(() =>
                    ProductsService.postApiV2ProductsLocation(locationId, {
                        products: selectedProducts,
                        buying: isBuying,
                    })
                );
            }

            // Step 4: Add services to location (if any selected)
            if (selectedServices && selectedServices.length > 0) {
                await withAuth(() =>
                    ServicesService.postApiV2ServicesLocation(locationId, {
                        services: selectedServices,
                        buying: isBuying,
                    })
                );
            }

            // Step 5: Save social media (if any)
            const accounts = {};
            Object.entries(socialMediaAccounts).forEach(([platform, handle]) => {
                if (handle.trim()) {
                    accounts[platform] = handle.trim();
                }
            });

            if (Object.keys(accounts).length > 0) {
                await withAuth(() =>
                    CorporateEntityService.postApiV2CorporateEntitySocialMedia(
                        corporateEntityId,
                        {
                            accounts,
                        }
                    )
                );
            }

            // Step 7: Save certifications (if supplier and certifications selected)
            // Note: This would require an API endpoint to add certifications to locations
            // For now, we'll skip this as the API structure for this is unclear
            // TODO: Add certifications to location when API endpoint is available

            // Step 8: Update userType, job_title, and profile_complete in database
            if (userId && userType) {
                await withAuth(() =>
                    UsersService.patchApiV2Users(userId, {
                        userType: userType,
                        job_title: finalRole,
                        profile_complete: true
                    })
                );
            }

            // Clear draft data from localStorage
            localStorage.removeItem('onboarding_draft');
            localStorage.removeItem('onboarding_step');
            localStorage.setItem('company_id', corporateEntityId.toString());
            localStorage.setItem('location_id', locationId.toString());

            setLoading(false);
            return { success: true, corporateEntityId, locationId };
        } catch (error) {
            console.error('Error submitting onboarding data:', error);

            // Try to extract more detailed error message
            let errorMessage = 'Failed to save onboarding data. Please try again.';

            if (error?.body) {
                // If error has a body, try to parse it
                try {
                    const errorBody = typeof error.body === 'string' ? JSON.parse(error.body) : error.body;
                    errorMessage = errorBody.message || errorBody.error || error.message || errorMessage;
                } catch (e) {
                    // If parsing fails, use the error message
                    errorMessage = error.message || errorMessage;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            // Log full error details for debugging
            console.error('Full error details:', {
                message: error.message,
                status: error.status,
                body: error.body,
                error: error,
            });

            setError(errorMessage);
            setLoading(false);
            throw error;
        }
    };

    const handleOnboardingComplete = () => {
        localStorage.removeItem('onboarding_step');
        localStorage.removeItem('onboarding_draft');
        navigate('/directory');
    };

    const toggleDiversityCert = (certId) => {
        setSelectedDiversityCerts((prev) =>
            prev.includes(certId) ? prev.filter((id) => id !== certId) : [...prev, certId]
        );
    };

    const toggleOtherCert = (certId) => {
        setSelectedOtherCerts((prev) =>
            prev.includes(certId) ? prev.filter((id) => id !== certId) : [...prev, certId]
        );
    };

    const toggleProduct = (productId) => {
        setSelectedProducts((prev) =>
            prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
        );
    };

    const toggleService = (serviceId) => {
        setSelectedServices((prev) =>
            prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]
        );
    };

    const renderStepIndicator = () => {
        // If user hasn't selected a type yet, show only the first step
        if (!userType) {
            return (
                <div className="flex items-center justify-center mb-8">
                    <div className="flex flex-col items-center">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold bg-brand-orange text-white">
                            1
                        </div>
                        <span className="text-xs mt-1 text-muted-foreground">User Type</span>
                    </div>
                </div>
            );
        }

        // For BROWSER, show 2 steps: User Type and Name
        if (userType === 'BROWSER') {
            return (
                <div className="flex items-center justify-center mb-8">
                    <div className="flex flex-col items-center">
                        <div
                            className={cn(
                                'w-10 h-10 rounded-full flex items-center justify-center font-semibold',
                                currentStep >= STEPS.BASIC_INFO
                                    ? 'bg-brand-orange text-white'
                                    : 'bg-gray-200 text-gray-600'
                            )}
                        >
                            1
                        </div>
                        <span className="text-xs mt-1 text-muted-foreground">User Type</span>
                    </div>
                    <div className={cn(
                        'w-16 h-1 mx-2',
                        currentStep > STEPS.BASIC_INFO ? 'bg-brand-orange' : 'bg-gray-200'
                    )} />
                    <div className="flex flex-col items-center">
                        <div
                            className={cn(
                                'w-10 h-10 rounded-full flex items-center justify-center font-semibold',
                                currentStep >= STEPS.PERSONAL_INFO
                                    ? 'bg-brand-orange text-white'
                                    : 'bg-gray-200 text-gray-600'
                            )}
                        >
                            2
                        </div>
                        <span className="text-xs mt-1 text-muted-foreground">Name</span>
                    </div>
                </div>
            );
        }

        // For BUYER or SUPPLIER, show all steps
        const steps = [
            { num: 1, label: 'User Type' },
            { num: 3, label: 'Company' },
            { num: 4, label: 'Location' },
            { num: 5, label: 'Products' },
            { num: 6, label: 'Role' },
        ];

        if (roleConfig?.requiresCertifications) {
            steps.push({ num: 7, label: 'Certifications' });
        }

        steps.push(
            { num: roleConfig?.requiresCertifications ? 8 : 7, label: 'Social' }
        );

        return (
            <div className="flex items-center justify-center mb-8">
                {steps.map((step, index) => (
                    <React.Fragment key={step.num}>
                        <div className="flex flex-col items-center">
                            <div
                                className={cn(
                                    'w-10 h-10 rounded-full flex items-center justify-center font-semibold',
                                    currentStep >= step.num
                                        ? 'bg-brand-orange text-white'
                                        : 'bg-gray-200 text-gray-600'
                                )}
                            >
                                {step.num}
                            </div>
                            <span className="text-xs mt-1 text-muted-foreground">{step.label}</span>
                        </div>
                        {index < steps.length - 1 && (
                            <div
                                className={cn(
                                    'w-16 h-1 mx-2',
                                    currentStep > step.num ? 'bg-brand-orange' : 'bg-gray-200'
                                )}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    // Show loading state while syncing user
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Loading your account...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl w-full space-y-8 bg-card p-8 rounded-lg shadow-md">
                <div>
                    <h2 className="text-3xl font-bold text-center text-foreground">
                        Complete Your Profile
                    </h2>
                    <p className="mt-2 text-center text-sm text-muted-foreground">
                        Let's set up your business profile
                    </p>
                </div>

                {renderStepIndicator()}

                {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                {success && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
                        {success}
                    </div>
                )}

                <form onSubmit={(e) => e.preventDefault()}>
                    {/* Step 1: User Type Selection */}
                    {currentStep === STEPS.BASIC_INFO && (
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    What brings you to ColorKarma?
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Select how you'll be using the platform
                                </p>
                            </div>
                            <div className="space-y-3">
                                <button
                                    type="button"
                                    onClick={() => setUserType('BUYER')}
                                    className={cn(
                                        'w-full p-4 text-left border-2 rounded-lg transition-colors',
                                        userType === 'BUYER'
                                            ? 'border-brand-orange bg-brand-orange-muted'
                                            : 'border-gray-200 hover:border-gray-300'
                                    )}
                                >
                                    <div className="font-semibold text-foreground">I'm a Buyer</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        Looking to find suppliers and get matched with products/services
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setUserType('SUPPLIER')}
                                    className={cn(
                                        'w-full p-4 text-left border-2 rounded-lg transition-colors',
                                        userType === 'SUPPLIER'
                                            ? 'border-brand-orange bg-brand-orange-muted'
                                            : 'border-gray-200 hover:border-gray-300'
                                    )}
                                >
                                    <div className="font-semibold text-foreground">I'm a Supplier</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        I'm a supplier/manufacturer looking to connect with buyers
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setUserType('BROWSER')}
                                    className={cn(
                                        'w-full p-4 text-left border-2 rounded-lg transition-colors',
                                        userType === 'BROWSER'
                                            ? 'border-brand-orange bg-brand-orange-muted'
                                            : 'border-gray-200 hover:border-gray-300'
                                    )}
                                >
                                    <div className="font-semibold text-foreground">I just want to browse</div>
                                    <div className="text-sm text-muted-foreground mt-1">
                                        I'm just exploring the directory without creating a profile
                                    </div>
                                </button>
                            </div>
                            <Button
                                type="submit"
                                onClick={handleBasicInfoSubmit}
                                disabled={loading || !userType}
                                className="w-full"
                            >
                                {loading ? 'Processing...' : 'Continue'}
                            </Button>
                        </div>
                    )}

                    {/* Step 2: Personal Info (BROWSER only) */}
                    {currentStep === STEPS.PERSONAL_INFO && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    Tell us your name
                                </h3>
                                <p className="text-sm text-muted-foreground mb-4">
                                    Please enter your first and last name
                                </p>
                            </div>
                            <div>
                                <label htmlFor="firstname" className="block text-sm font-medium text-foreground">
                                    First Name *
                                </label>
                                <Input
                                    id="firstname"
                                    type="text"
                                    value={firstname}
                                    onChange={(e) => setFirstname(e.target.value)}
                                    placeholder="Enter your first name"
                                    className="mt-1"
                                    required
                                />
                            </div>
                            <div>
                                <label htmlFor="lastname" className="block text-sm font-medium text-foreground">
                                    Last Name *
                                </label>
                                <Input
                                    id="lastname"
                                    type="text"
                                    value={lastname}
                                    onChange={(e) => setLastname(e.target.value)}
                                    placeholder="Enter your last name"
                                    className="mt-1"
                                    required
                                />
                            </div>
                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCurrentStep(STEPS.BASIC_INFO)}
                                    className="flex-1"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handlePersonalInfoSubmit}
                                    disabled={loading || !firstname.trim() || !lastname.trim()}
                                    className="flex-1"
                                >
                                    {loading ? 'Saving...' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Corporate Entity */}
                    {currentStep === STEPS.CORPORATE_ENTITY && (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="legalName" className="block text-sm font-medium text-foreground">
                                    Legal Company Name *
                                </label>
                                <Input
                                    id="legalName"
                                    type="text"
                                    value={legalName}
                                    onChange={(e) => setLegalName(e.target.value)}
                                    placeholder="Enter your company's legal name"
                                    className="mt-1"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="website" className="block text-sm font-medium text-foreground">
                                    Website URL *
                                </label>
                                <Input
                                    id="website"
                                    type="url"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    onBlur={(e) => {
                                        let value = e.target.value.trim();
                                        // Auto-fill https:// if missing protocol
                                        if (value && !value.startsWith('http://') && !value.startsWith('https://')) {
                                            setWebsite('https://' + value);
                                        }
                                    }}
                                    placeholder="https://example.com"
                                    className="mt-1"
                                    required
                                />
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Enter your website URL (http:// or https:// will be added automatically if missing)
                                </p>
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-foreground">
                                    Phone Number *
                                </label>
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => {
                                        // Extract only digits from input
                                        const digits = e.target.value.replace(/\D/g, '');
                                        // Format using AsYouType (defaults to US format)
                                        const formatter = new AsYouType('US');
                                        const formatted = formatter.input(digits);
                                        setPhone(formatted);
                                    }}
                                    placeholder="(555) 123-4567"
                                    className="mt-1"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Company Size *
                                </label>
                                <div className="space-y-2">
                                    {[
                                        { value: 'MICRO', label: 'Micro (1-10 employees)' },
                                        { value: 'SMALL', label: 'Small (11-50 employees)' },
                                        { value: 'MEDIUM', label: 'Medium (51-200 employees)' },
                                        { value: 'LARGE', label: 'Large (201-1000 employees)' },
                                        { value: 'ENTERPRISE', label: 'Enterprise (1000+ employees)' },
                                    ].map((size) => (
                                        <button
                                            key={size.value}
                                            type="button"
                                            onClick={() => setCompanySize(size.value)}
                                            className={cn(
                                                'w-full p-3 text-left border-2 rounded-lg transition-colors text-foreground',
                                                companySize === size.value
                                                    ? 'border-brand-orange bg-brand-orange-muted'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            )}
                                        >
                                            {size.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Industry *
                                </label>
                                {industry && (
                                    <div className="mb-2 p-3 bg-brand-orange-muted border border-brand-orange rounded-lg">
                                        <div className="text-sm text-muted-foreground">Selected industry:</div>
                                        <div className="font-semibold text-foreground">{industry}</div>
                                    </div>
                                )}
                                <div className="relative industry-dropdown-container">
                                    <Input
                                        type="text"
                                        placeholder={industry ? "Search for a different industry..." : "Search industries..."}
                                        value={industrySearch}
                                        onChange={(e) => {
                                            setIndustrySearch(e.target.value);
                                            setShowIndustryDropdown(true);
                                        }}
                                        onFocus={() => setShowIndustryDropdown(true)}
                                        className="mb-2"
                                    />
                                    {showIndustryDropdown && (
                                        <div className="absolute z-10 w-full max-h-60 overflow-y-auto border rounded-lg bg-card shadow-lg">
                                            {filteredIndustries.map((ind) => (
                                                <button
                                                    key={ind.indname}
                                                    type="button"
                                                    onClick={() => {
                                                        setIndustry(ind.indname);
                                                        setIndustrySearch('');
                                                        setShowIndustryDropdown(false);
                                                    }}
                                                    className={cn(
                                                        'w-full p-3 text-left hover:bg-accent border-b last:border-b-0 text-foreground',
                                                        industry === ind.indname && 'bg-brand-orange-muted border-brand-orange'
                                                    )}
                                                >
                                                    {ind.indname}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {moqQuestion && (
                                <div>
                                    <label htmlFor="moq" className="block text-sm font-medium text-foreground">
                                        {moqQuestion}
                                    </label>
                                    <Input
                                        id="moq"
                                        type="text"
                                        value={moq}
                                        onChange={(e) => setMoq(e.target.value)}
                                        placeholder="Enter MOQ or leave blank"
                                        className="mt-1"
                                    />
                                </div>
                            )}

                            <Button
                                type="submit"
                                onClick={handleCorporateEntitySubmit}
                                disabled={loading}
                                className="w-full"
                            >
                                {loading ? 'Creating...' : 'Continue'}
                            </Button>
                        </div>
                    )}

                    {/* Step 2: Location */}
                    {currentStep === STEPS.LOCATION && (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="address" className="block text-sm font-medium text-foreground">
                                    Street Address *
                                </label>
                                <Input
                                    id="address"
                                    type="text"
                                    value={address}
                                    onChange={(e) => setAddress(e.target.value)}
                                    placeholder="123 Main Street"
                                    className="mt-1"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-foreground">
                                    City *
                                </label>
                                <Input
                                    id="city"
                                    type="text"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder="New York"
                                    className="mt-1"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-foreground">
                                    Country *
                                </label>
                                <select
                                    id="country"
                                    value={countryCode}
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-brand-orange focus:ring-brand-orange p-2 border"
                                    required
                                >
                                    <option value="">Select a country</option>
                                    {countries.map((country) => (
                                        <option key={country.country_code} value={country.country_code}>
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {countryCode && states.length > 0 && (
                                <div>
                                    <label htmlFor="state" className="block text-sm font-medium text-foreground">
                                        State/Province
                                    </label>
                                    <select
                                        id="state"
                                        value={state}
                                        onChange={(e) => setState(e.target.value)}
                                        className="mt-1 block w-full rounded-md border-input bg-background text-foreground shadow-sm focus:border-brand-orange focus:ring-brand-orange p-2 border"
                                    >
                                        <option value="">Select a state/province</option>
                                        {states.map((s) => (
                                            <option key={s.state_code} value={s.state_code}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label htmlFor="postalCode" className="block text-sm font-medium text-foreground">
                                    Postal/ZIP Code
                                </label>
                                <Input
                                    id="postalCode"
                                    type="text"
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.target.value)}
                                    placeholder="10001"
                                    className="mt-1"
                                />
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCurrentStep(STEPS.CORPORATE_ENTITY)}
                                    className="flex-1"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handleLocationSubmit}
                                    disabled={loading}
                                    className="flex-1"
                                >
                                    {loading ? 'Saving...' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Products & Services */}
                    {currentStep === STEPS.PRODUCTS && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground mb-4">
                                Select the products and services your location offers or is looking to buy (optional)
                            </p>

                            {/* Products Section */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-3">
                                    Products (Optional)
                                </label>
                                {products.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">Loading products...</p>
                                ) : (
                                    <>
                                        <div className="max-h-60 overflow-y-auto border rounded-lg space-y-2 p-2 mb-3">
                                            {products.map((product) => (
                                                <button
                                                    key={product.id}
                                                    type="button"
                                                    onClick={() => toggleProduct(product.id)}
                                                    className={cn(
                                                        'w-full p-3 text-left border rounded-lg transition-colors text-foreground',
                                                        selectedProducts.includes(product.id)
                                                            ? 'border-brand-orange bg-brand-orange-muted'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    )}
                                                >
                                                    <div className="font-semibold">
                                                        {product.itemname || product.categoryname || 'Product'}
                                                    </div>
                                                    {product.categoryname && product.itemname && (
                                                        <div className="text-sm text-muted-foreground">
                                                            {product.categoryname}
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Services Section */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-3">
                                    Services (Optional)
                                </label>
                                {services.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">Loading services...</p>
                                ) : (
                                    <>
                                        <div className="max-h-60 overflow-y-auto border rounded-lg space-y-2 p-2 mb-3">
                                            {services.map((service) => (
                                                <button
                                                    key={service.id}
                                                    type="button"
                                                    onClick={() => toggleService(service.id)}
                                                    className={cn(
                                                        'w-full p-3 text-left border rounded-lg transition-colors text-foreground',
                                                        selectedServices.includes(service.id)
                                                            ? 'border-brand-orange bg-brand-orange-muted'
                                                            : 'border-gray-200 hover:border-gray-300'
                                                    )}
                                                >
                                                    <div className="font-semibold">
                                                        {service.itemname || service.categoryname || 'Service'}
                                                    </div>
                                                    {service.categoryname && service.itemname && (
                                                        <div className="text-sm text-muted-foreground">
                                                            {service.categoryname}
                                                        </div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCurrentStep(STEPS.LOCATION)}
                                    className="flex-1"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handleProductsSubmit}
                                    disabled={loading}
                                    className="flex-1"
                                >
                                    {loading ? 'Saving...' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Manager Role */}
                    {currentStep === STEPS.MANAGER_ROLE && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Manager Role
                                </label>
                                {role && (
                                    <div className="mb-4 p-4 bg-brand-orange-muted border border-brand-orange rounded-lg">
                                        <div className="text-sm text-muted-foreground mb-1">Your role from signup:</div>
                                        <div className="font-semibold text-lg text-foreground">{role}</div>
                                    </div>
                                )}
                                <p className="text-sm text-muted-foreground mb-4">
                                    {role
                                        ? "We'll use the role you selected during signup. You can change it below if needed."
                                        : "Please enter your role/position"}
                                </p>
                                <Input
                                    id="managerRole"
                                    type="text"
                                    value={managerRole || role || ''}
                                    onChange={(e) => {
                                        setManagerRole(e.target.value);
                                        setCustomRole('');
                                    }}
                                    placeholder="Enter your role"
                                    className="mt-1"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setManagerRole('other');
                                    }}
                                    className="mt-2 text-sm text-brand-orange hover:underline"
                                >
                                    Use a different role
                                </button>
                                {managerRole === 'other' && (
                                    <Input
                                        type="text"
                                        placeholder="Enter your custom role"
                                        value={customRole}
                                        onChange={(e) => setCustomRole(e.target.value)}
                                        className="mt-2"
                                        required
                                    />
                                )}
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCurrentStep(STEPS.PRODUCTS)}
                                    className="flex-1"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handleManagerRoleSubmit}
                                    disabled={loading}
                                    className="flex-1"
                                >
                                    {loading ? 'Saving...' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 5: Certifications (Suppliers only) */}
                    {currentStep === STEPS.CERTIFICATIONS && roleConfig?.requiresCertifications && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-3">
                                    Diversity Certifications (Optional)
                                </label>
                                <div className="max-h-60 overflow-y-auto border rounded-lg space-y-2 p-2">
                                    {availableDiversityCerts.map((cert) => (
                                        <button
                                            key={cert.id}
                                            type="button"
                                            onClick={() => toggleDiversityCert(cert.id)}
                                            className={cn(
                                                'w-full p-3 text-left border rounded-lg transition-colors text-foreground',
                                                selectedDiversityCerts.includes(cert.id)
                                                    ? 'border-brand-orange bg-brand-orange-muted'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            )}
                                        >
                                            <div className="font-semibold">{cert.certname}</div>
                                            {cert.description && (
                                                <div className="text-sm text-muted-foreground">{cert.description}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-foreground mb-3">
                                    Other Certifications (Optional)
                                </label>
                                <div className="max-h-60 overflow-y-auto border rounded-lg space-y-2 p-2">
                                    {availableOtherCerts.map((cert) => (
                                        <button
                                            key={cert.id}
                                            type="button"
                                            onClick={() => toggleOtherCert(cert.id)}
                                            className={cn(
                                                'w-full p-3 text-left border rounded-lg transition-colors text-foreground',
                                                selectedOtherCerts.includes(cert.id)
                                                    ? 'border-brand-orange bg-brand-orange-muted'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            )}
                                        >
                                            <div className="font-semibold">{cert.certname}</div>
                                            {cert.description && (
                                                <div className="text-sm text-muted-foreground">{cert.description}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCurrentStep(STEPS.MANAGER_ROLE)}
                                    className="flex-1"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handleCertificationsSubmit}
                                    className="flex-1"
                                >
                                    Continue
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Step 6: Social Media */}
                    {currentStep === STEPS.SOCIAL_MEDIA && (
                        <div className="space-y-4">
                            <p className="text-sm text-muted-foreground mb-4">
                                Add your social media profiles (all optional)
                            </p>

                            {['linkedin', 'facebook', 'twitter', 'instagram', 'youtube'].map((platform) => (
                                <div key={platform}>
                                    <label
                                        htmlFor={platform}
                                        className="block text-sm font-medium text-foreground capitalize"
                                    >
                                        {platform === 'linkedin' ? 'LinkedIn' : platform}
                                    </label>
                                    <Input
                                        id={platform}
                                        type="text"
                                        value={socialMediaAccounts[platform]}
                                        onChange={(e) =>
                                            setSocialMediaAccounts({
                                                ...socialMediaAccounts,
                                                [platform]: e.target.value,
                                            })
                                        }
                                        placeholder={`Enter your ${platform} handle or URL`}
                                        className="mt-1"
                                    />
                                </div>
                            ))}

                            <div className="flex gap-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        const prevStep = getPreviousStep(currentStep, userType);
                                        if (prevStep) {
                                            setCurrentStep(prevStep);
                                        }
                                    }}
                                    className="flex-1"
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    onClick={handleSocialMediaSubmit}
                                    disabled={loading}
                                    className="flex-1"
                                >
                                    {loading ? 'Saving...' : 'Continue'}
                                </Button>
                            </div>
                        </div>
                    )}

                </form>
            </div>
        </div>
    );
}

export default Onboarding;

